import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Typography,
  Button,
  Paper,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  Divider,
  Alert,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AppsIcon from '@mui/icons-material/Apps';
import SettingsIcon from '@mui/icons-material/Settings';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useApp } from '../../hooks/useApp';
import { useAuth } from '../../hooks/useAuth';
import { useDocs } from '../../hooks/useDocs';
import { useStorage } from '../../hooks/useStorage';
import colorUtils from '../../Utils/ColorUtil';
import { AGENT_ICONS } from '../../components/AgentNode/AgentNode';
import './ProveitEdit.css';

// ProveIt megafactory ID
const PROVEIT_MEGAFACTORY_ID = 'zvvIoi5j80myiEVJLVj8';
// Node type constants
const NODE_TYPES = {
  BEACON: 'beacon',
  DATA_TAG: 'dataTag',
  AGENT: 'agent',
};

const ProveitEdit = () => {
  const navigate = useNavigate();
  useAuth();
  const { vtecAssetsUnsorted: vtecAssets } = useApp();
  const { updateAsset } = useDocs('vtec_assets');
  const { uploadFile, isPending: isUploading, error: uploadError } = useStorage();
  
  // Refs for drag-and-drop
  const previewContainerRef = useRef(null);
  const imageRef = useRef(null);
  const uploadFileRef = useRef(null);
  const nodeRefs = useRef({}); // Store refs to node elements for offset calculation
  
  // Loading and UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectedUploadFile, setSelectedUploadFile] = useState(null);
  
  // Node editing state
  const [selectedNode, setSelectedNode] = useState(null); // { type, id, data }
  const [draggingNode, setDraggingNode] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 }); // Offset from node center to click point
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingNode, setEditingNode] = useState(null);
  
  // Original data (for comparison and discard)
  const [originalMegaFactory, setOriginalMegaFactory] = useState(null);
  const [originalFactories, setOriginalFactories] = useState({});
  
  // Editable state
  const [editedMegaFactory, setEditedMegaFactory] = useState(null);
  const [editedFactories, setEditedFactories] = useState({});
  
  // Track if there are unsaved changes
  const isDirty = useMemo(() => {
    if (!originalMegaFactory || !editedMegaFactory) return false;
    
    const megaFactoryChanged = JSON.stringify(originalMegaFactory) !== JSON.stringify(editedMegaFactory);
    const factoriesChanged = JSON.stringify(originalFactories) !== JSON.stringify(editedFactories);
    
    return megaFactoryChanged || factoriesChanged;
  }, [originalMegaFactory, editedMegaFactory, originalFactories, editedFactories]);

  // Load megafactory and subfactories
  useEffect(() => {
    if (!vtecAssets) return;

    const megafactory = vtecAssets.find(
      (asset) => asset.id === PROVEIT_MEGAFACTORY_ID && asset.Type === 'megafactory'
    );

    if (megafactory) {
      const megafactoryCopy = JSON.parse(JSON.stringify(megafactory));
      
      if (!megafactoryCopy.mqttConfig) {
        // Read default MQTT config from the megafactory's unsConfig (Firestore)
        const defaultConfig = megafactoryCopy.unsConfig?.mqttConfig || {};
        megafactoryCopy.mqttConfig = {
          broker: defaultConfig.broker || '',
          username: defaultConfig.username || '',
          password: defaultConfig.password || '',
          topicPrefix: defaultConfig.topicPrefix || '',
        };
      }
      
      setOriginalMegaFactory(JSON.parse(JSON.stringify(megafactoryCopy)));
      setEditedMegaFactory(megafactoryCopy);
      
      const factories = {};
      const suites = megafactory.suites || [];
      suites.forEach((suite) => {
        if (suite.factoryId) {
          const factory = vtecAssets.find((asset) => asset.id === suite.factoryId);
          if (factory) {
            factories[suite.factoryId] = JSON.parse(JSON.stringify(factory));
          }
        }
      });
      setOriginalFactories(JSON.parse(JSON.stringify(factories)));
      setEditedFactories(factories);
    }
    
    setLoading(false);
  }, [vtecAssets]);

  // Get list of subfactories for tabs
  const subfactories = useMemo(() => {
    if (!editedMegaFactory) return [];
    return (editedMegaFactory.suites || [])
      .map((suite) => ({
        ...suite,
        factory: editedFactories[suite.factoryId],
      }))
      .filter((suite) => suite.factory);
  }, [editedMegaFactory, editedFactories]);

  // Current factory helpers
  const currentFactory = subfactories[activeTab]?.factory;
  const currentFactoryId = subfactories[activeTab]?.factoryId;
  
  const getFactoryImageUrl = (factory) => {
    if (!factory) return null;
    if (factory.mediaUrl) return factory.mediaUrl;
    if (factory.img) return factory.img;
    if (factory.Image) return factory.Image;
    return null;
  };

  const currentFactoryImageUrl = getFactoryImageUrl(currentFactory);

  const selectedUploadExtension = useMemo(() => {
    if (!selectedUploadFile?.name) return '';
    const nameParts = selectedUploadFile.name.toLowerCase().split('.');
    return nameParts.length > 1 ? nameParts.pop() : '';
  }, [selectedUploadFile]);

  const isSelectedVideoUpload = useMemo(() => {
    if (!selectedUploadFile) return false;
    if (selectedUploadFile.type?.startsWith('video/')) return true;
    return ['webm', 'mp4', 'mov', 'mkv'].includes(selectedUploadExtension);
  }, [selectedUploadFile, selectedUploadExtension]);

  const isLikelyAlphaCompatibleUpload = useMemo(() => (
    ['webm', 'mkv'].includes(selectedUploadExtension)
  ), [selectedUploadExtension]);

  // Handle MQTT config change
  const handleMqttConfigChange = useCallback((field, value) => {
    setEditedMegaFactory((prev) => ({
      ...prev,
      mqttConfig: { ...prev.mqttConfig, [field]: value },
    }));
  }, []);

  // Generate unique ID
  const generateId = () => `node-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Update factory data
  const updateFactory = useCallback((factoryId, updates) => {
    setEditedFactories((prev) => ({
      ...prev,
      [factoryId]: { ...prev[factoryId], ...updates },
    }));
  }, []);

  // === NODE MANAGEMENT ===
  
  // Add new data tag
  const handleAddDataTag = useCallback(() => {
    if (!currentFactoryId) return;
    const newTag = {
      id: generateId(),
      label: 'New Tag',
      topicPath: '',
      position: { x: 50, y: 50 },
      style: { color: '#00C853' },
    };
    const currentTags = editedFactories[currentFactoryId]?.dataTags || [];
    updateFactory(currentFactoryId, { dataTags: [...currentTags, newTag] });
    setSelectedNode({ type: NODE_TYPES.DATA_TAG, id: newTag.id, data: newTag });
  }, [currentFactoryId, editedFactories, updateFactory]);

  // Add new agent
  const handleAddAgent = useCallback(() => {
    if (!currentFactoryId) return;
    const newAgent = {
      id: generateId(),
      name: 'New Agent',
      topicBase: '',
      position: { x: 50, y: 50 },
      style: { color: '#1976d2' },
      icon: 'public',
      simulatedThoughts: [],
    };
    const currentAgents = editedFactories[currentFactoryId]?.agents || [];
    updateFactory(currentFactoryId, { agents: [...currentAgents, newAgent] });
    setSelectedNode({ type: NODE_TYPES.AGENT, id: newAgent.id, data: newAgent });
  }, [currentFactoryId, editedFactories, updateFactory]);

  // Add new beacon (Children item)
  const handleAddBeacon = useCallback(() => {
    if (!currentFactoryId) return;
    const newBeacon = {
      id: generateId(),
      Title: 'New App',
      Top: '50%',
      Left: '50%',
      color: '#2196F3',
    };
    const currentChildren = editedFactories[currentFactoryId]?.Children || [];
    updateFactory(currentFactoryId, { Children: [...currentChildren, newBeacon] });
    setSelectedNode({ type: NODE_TYPES.BEACON, id: newBeacon.id, data: newBeacon });
  }, [currentFactoryId, editedFactories, updateFactory]);

  // Delete node
  const handleDeleteNode = useCallback((type, id) => {
    if (!currentFactoryId) return;
    
    if (type === NODE_TYPES.DATA_TAG) {
      const currentTags = editedFactories[currentFactoryId]?.dataTags || [];
      updateFactory(currentFactoryId, { dataTags: currentTags.filter(t => t.id !== id) });
    } else if (type === NODE_TYPES.AGENT) {
      const currentAgents = editedFactories[currentFactoryId]?.agents || [];
      updateFactory(currentFactoryId, { agents: currentAgents.filter(a => a.id !== id) });
    } else if (type === NODE_TYPES.BEACON) {
      const currentChildren = editedFactories[currentFactoryId]?.Children || [];
      updateFactory(currentFactoryId, { Children: currentChildren.filter(c => c.id !== id) });
    }
    
    if (selectedNode?.id === id) setSelectedNode(null);
  }, [currentFactoryId, editedFactories, updateFactory, selectedNode]);

  // Update node position
  const updateNodePosition = useCallback((type, id, newPosition) => {
    if (!currentFactoryId) return;
    
    if (type === NODE_TYPES.DATA_TAG) {
      const currentTags = editedFactories[currentFactoryId]?.dataTags || [];
      updateFactory(currentFactoryId, {
        dataTags: currentTags.map(t => t.id === id ? { ...t, position: newPosition } : t),
      });
    } else if (type === NODE_TYPES.AGENT) {
      const currentAgents = editedFactories[currentFactoryId]?.agents || [];
      updateFactory(currentFactoryId, {
        agents: currentAgents.map(a => a.id === id ? { ...a, position: newPosition } : a),
      });
    } else if (type === NODE_TYPES.BEACON) {
      const currentChildren = editedFactories[currentFactoryId]?.Children || [];
      updateFactory(currentFactoryId, {
        Children: currentChildren.map(c => c.id === id ? { ...c, Top: `${newPosition.y}%`, Left: `${newPosition.x}%` } : c),
      });
    }
  }, [currentFactoryId, editedFactories, updateFactory]);

  // Update node data
  const updateNodeData = useCallback((type, id, updates) => {
    if (!currentFactoryId) return;
    
    if (type === NODE_TYPES.DATA_TAG) {
      const currentTags = editedFactories[currentFactoryId]?.dataTags || [];
      updateFactory(currentFactoryId, {
        dataTags: currentTags.map(t => t.id === id ? { ...t, ...updates } : t),
      });
    } else if (type === NODE_TYPES.AGENT) {
      const currentAgents = editedFactories[currentFactoryId]?.agents || [];
      updateFactory(currentFactoryId, {
        agents: currentAgents.map(a => a.id === id ? { ...a, ...updates } : a),
      });
    } else if (type === NODE_TYPES.BEACON) {
      const currentChildren = editedFactories[currentFactoryId]?.Children || [];
      updateFactory(currentFactoryId, {
        Children: currentChildren.map(c => c.id === id ? { ...c, ...updates } : c),
      });
    }
    
    // Update selected node if it's the one being edited
    if (selectedNode?.id === id) {
      setSelectedNode(prev => ({ ...prev, data: { ...prev.data, ...updates } }));
    }
  }, [currentFactoryId, editedFactories, updateFactory, selectedNode]);

  // === DRAG AND DROP WITH OFFSET TRACKING ===
  
  const getPositionFromEvent = useCallback((e, offset = { x: 0, y: 0 }) => {
    if (!imageRef.current) return { x: 50, y: 50 };
    
    const rect = imageRef.current.getBoundingClientRect();
    // Apply offset to get the position where the node center should be
    const adjustedX = e.clientX - offset.x;
    const adjustedY = e.clientY - offset.y;
    
    const x = ((adjustedX - rect.left) / rect.width) * 100;
    const y = ((adjustedY - rect.top) / rect.height) * 100;
    
    return {
      x: Math.max(2, Math.min(98, x)),
      y: Math.max(2, Math.min(98, y)),
    };
  }, []);

  const handleMouseDown = useCallback((e, type, id) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Calculate offset from the node's anchor point to the click position
    const nodeElement = nodeRefs.current[id];
    if (nodeElement) {
      const nodeRect = nodeElement.getBoundingClientRect();
      // For nodes, the anchor is typically the bottom center (where the dot is)
      // Get the dot element position
      const dotElement = nodeElement.querySelector('.edit-node-dot');
      if (dotElement) {
        const dotRect = dotElement.getBoundingClientRect();
        const dotCenterX = dotRect.left + dotRect.width / 2;
        const dotCenterY = dotRect.top + dotRect.height / 2;
        setDragOffset({
          x: e.clientX - dotCenterX,
          y: e.clientY - dotCenterY,
        });
      } else {
        // Fallback to node center
        const nodeCenterX = nodeRect.left + nodeRect.width / 2;
        const nodeCenterY = nodeRect.top + nodeRect.height / 2;
        setDragOffset({
          x: e.clientX - nodeCenterX,
          y: e.clientY - nodeCenterY,
        });
      }
    } else {
      setDragOffset({ x: 0, y: 0 });
    }
    
    setDraggingNode({ type, id });
    
    // Find and set selected node
    let nodeData;
    if (type === NODE_TYPES.DATA_TAG) {
      nodeData = editedFactories[currentFactoryId]?.dataTags?.find(t => t.id === id);
    } else if (type === NODE_TYPES.AGENT) {
      nodeData = editedFactories[currentFactoryId]?.agents?.find(a => a.id === id);
    } else if (type === NODE_TYPES.BEACON) {
      nodeData = editedFactories[currentFactoryId]?.Children?.find(c => c.id === id);
    }
    if (nodeData) {
      setSelectedNode({ type, id, data: nodeData });
    }
  }, [currentFactoryId, editedFactories]);

  const handleMouseMove = useCallback((e) => {
    if (!draggingNode) return;
    
    // Use the offset to calculate the correct position
    const newPosition = getPositionFromEvent(e, dragOffset);
    updateNodePosition(draggingNode.type, draggingNode.id, newPosition);
  }, [draggingNode, dragOffset, getPositionFromEvent, updateNodePosition]);

  const handleMouseUp = useCallback(() => {
    setDraggingNode(null);
    setDragOffset({ x: 0, y: 0 });
  }, []);

  // Attach global mouse listeners for drag
  useEffect(() => {
    if (draggingNode) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [draggingNode, handleMouseMove, handleMouseUp]);

  // Click on node to select
  const handleNodeClick = useCallback((e, type, id) => {
    e.stopPropagation();
    let nodeData;
    if (type === NODE_TYPES.DATA_TAG) {
      nodeData = editedFactories[currentFactoryId]?.dataTags?.find(t => t.id === id);
    } else if (type === NODE_TYPES.AGENT) {
      nodeData = editedFactories[currentFactoryId]?.agents?.find(a => a.id === id);
    } else if (type === NODE_TYPES.BEACON) {
      nodeData = editedFactories[currentFactoryId]?.Children?.find(c => c.id === id);
    }
    if (nodeData) {
      setSelectedNode({ type, id, data: nodeData });
    }
  }, [currentFactoryId, editedFactories]);

  // Clear selection when clicking on preview background
  const handlePreviewClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  // === EDIT DIALOG ===
  
  const openEditDialog = useCallback((type, id) => {
    let nodeData;
    if (type === NODE_TYPES.DATA_TAG) {
      nodeData = editedFactories[currentFactoryId]?.dataTags?.find(t => t.id === id);
    } else if (type === NODE_TYPES.AGENT) {
      nodeData = editedFactories[currentFactoryId]?.agents?.find(a => a.id === id);
    } else if (type === NODE_TYPES.BEACON) {
      nodeData = editedFactories[currentFactoryId]?.Children?.find(c => c.id === id);
    }
    if (nodeData) {
      setEditingNode({ type, id, data: { ...nodeData } });
      setEditDialogOpen(true);
    }
  }, [currentFactoryId, editedFactories]);

  const handleSaveEditDialog = useCallback(() => {
    if (!editingNode) return;
    updateNodeData(editingNode.type, editingNode.id, editingNode.data);
    setEditDialogOpen(false);
    setEditingNode(null);
  }, [editingNode, updateNodeData]);

  // === SAVE/DISCARD ===
  
  const handleDiscard = useCallback(() => {
    setEditedMegaFactory(JSON.parse(JSON.stringify(originalMegaFactory)));
    setEditedFactories(JSON.parse(JSON.stringify(originalFactories)));
    setSelectedNode(null);
    setSnackbar({ open: true, message: 'Changes discarded', severity: 'info' });
  }, [originalMegaFactory, originalFactories]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const savePromises = [];
      
      if (JSON.stringify(originalMegaFactory) !== JSON.stringify(editedMegaFactory)) {
        savePromises.push(updateAsset(editedMegaFactory));
      }
      
      for (const [factoryId, factory] of Object.entries(editedFactories)) {
        if (JSON.stringify(originalFactories[factoryId]) !== JSON.stringify(factory)) {
          savePromises.push(updateAsset(factory));
        }
      }
      
      await Promise.all(savePromises);
      
      setOriginalMegaFactory(JSON.parse(JSON.stringify(editedMegaFactory)));
      setOriginalFactories(JSON.parse(JSON.stringify(editedFactories)));
      
      setSnackbar({ open: true, message: 'All changes saved successfully!', severity: 'success' });
    } catch (error) {
      console.error('Error saving changes:', error);
      setSnackbar({ open: true, message: 'Error saving changes. Please try again.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  }, [editedFactories, editedMegaFactory, originalFactories, originalMegaFactory, updateAsset]);

  // Warn before leaving
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // Clear selection when switching tabs
  useEffect(() => {
    setSelectedNode(null);
    nodeRefs.current = {};
    setSelectedUploadFile(null);
  }, [activeTab]);

  // Handle file upload to Firebase Storage
  const handleFileUpload = useCallback(async () => {
    if (!selectedUploadFile || !currentFactoryId) return;
    try {
      const downloadUrl = await uploadFile(selectedUploadFile, 'proveit-media');
      if (downloadUrl) {
        updateFactory(currentFactoryId, {
          mediaUrl: downloadUrl,
        });
        setSelectedUploadFile(null);
        if (uploadFileRef.current) uploadFileRef.current.value = '';
        setSnackbar({ open: true, message: 'File uploaded and Media URL updated!', severity: 'success' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Upload failed. Please try again.', severity: 'error' });
    }
  }, [selectedUploadFile, currentFactoryId, uploadFile, updateFactory]);

  // === RENDER FULL NODE COMPONENTS ===
  
  // Render App Beacon (pill-shaped button style)
  const renderBeaconNode = (beacon, isSelected) => {
    const topVal = parseFloat(beacon.Top) || 50;
    const leftVal = parseFloat(beacon.Left) || 50;
    const beaconColor = beacon.color || '#2196F3';
    const fontColor = colorUtils.isColorDark(beaconColor) ? '#FFFFFF' : '#000000';
    const isDragging = draggingNode?.id === beacon.id;
    
    return (
      <div
        key={beacon.id}
        ref={(el) => { nodeRefs.current[beacon.id] = el; }}
        className={`edit-node-wrapper edit-beacon-wrapper ${isSelected ? 'selected' : ''} ${isDragging ? 'dragging' : ''}`}
        style={{
          left: `${leftVal}%`,
          top: `${topVal}%`,
        }}
        onMouseDown={(e) => handleMouseDown(e, NODE_TYPES.BEACON, beacon.id)}
        onClick={(e) => handleNodeClick(e, NODE_TYPES.BEACON, beacon.id)}
        onDoubleClick={() => openEditDialog(NODE_TYPES.BEACON, beacon.id)}
      >
        {/* Beacon pill */}
        <div 
          className="edit-beacon-pill"
          style={{
            backgroundColor: beaconColor,
            color: fontColor,
            borderColor: fontColor,
          }}
        >
          {beacon.Title || 'App'}
        </div>
        {/* Outer ring */}
        <div className="edit-node-ring" />
        {/* Connection dot */}
        <div className="edit-node-dot" style={{ backgroundColor: beaconColor }} />
        {/* Selection indicator */}
        {isSelected && <div className="edit-node-selection-ring" style={{ borderColor: beaconColor }} />}
      </div>
    );
  };

  // Render Data Tag (card with value display)
  const renderDataTagNode = (tag, isSelected) => {
    const position = tag.position || { x: 50, y: 50 };
    const color = tag.style?.color || '#00C853';
    const isDragging = draggingNode?.id === tag.id;
    
    return (
      <div
        key={tag.id}
        ref={(el) => { nodeRefs.current[tag.id] = el; }}
        className={`edit-node-wrapper edit-datatag-wrapper ${isSelected ? 'selected' : ''} ${isDragging ? 'dragging' : ''}`}
        style={{
          left: `${position.x}%`,
          top: `${position.y}%`,
        }}
        onMouseDown={(e) => handleMouseDown(e, NODE_TYPES.DATA_TAG, tag.id)}
        onClick={(e) => handleNodeClick(e, NODE_TYPES.DATA_TAG, tag.id)}
        onDoubleClick={() => openEditDialog(NODE_TYPES.DATA_TAG, tag.id)}
      >
        {/* Tag card */}
        <div 
          className="edit-datatag-card"
          style={{
            '--tag-color': color,
          }}
        >
          <span className="edit-datatag-label">{tag.label || 'Tag'}</span>
          <span className="edit-datatag-value" style={{ color }}>---</span>
        </div>
        {/* Pointer */}
        <div className="edit-datatag-pointer" />
        {/* Outer ring */}
        <div className="edit-node-ring" style={{ backgroundColor: '#00263E' }} />
        {/* Connection dot */}
        <div className="edit-node-dot" style={{ backgroundColor: color }} />
        {/* Selection indicator */}
        {isSelected && <div className="edit-node-selection-ring" style={{ borderColor: color }} />}
      </div>
    );
  };

  // Render AI Agent (card with header and status)
  const renderAgentNode = (agent, isSelected) => {
    const position = agent.position || { x: 50, y: 50 };
    const color = agent.style?.color || '#1976d2';
    const isDragging = draggingNode?.id === agent.id;
    const iconKey = agent.icon || 'public';
    const iconData = AGENT_ICONS[iconKey] || AGENT_ICONS.public;
    
    return (
      <div
        key={agent.id}
        ref={(el) => { nodeRefs.current[agent.id] = el; }}
        className={`edit-node-wrapper edit-agent-wrapper ${isSelected ? 'selected' : ''} ${isDragging ? 'dragging' : ''}`}
        style={{
          left: `${position.x}%`,
          top: `${position.y}%`,
        }}
        onMouseDown={(e) => handleMouseDown(e, NODE_TYPES.AGENT, agent.id)}
        onClick={(e) => handleNodeClick(e, NODE_TYPES.AGENT, agent.id)}
        onDoubleClick={() => openEditDialog(NODE_TYPES.AGENT, agent.id)}
      >
        {/* Agent card */}
        <div 
          className="edit-agent-card"
          style={{
            '--agent-color': color,
          }}
        >
          <div className="edit-agent-header" style={{ backgroundColor: color }}>
            <svg viewBox={iconData.viewBox} fill="currentColor" width="16" height="16" style={{ opacity: 0.9 }}>
              <path d={iconData.path} />
            </svg>
            <span>{agent.name || 'Agent'}</span>
          </div>
          <div className="edit-agent-body">
            <div className="edit-agent-status">
              <span className="edit-agent-status-dot" />
              Idle
            </div>
            <div className="edit-agent-hint">Waiting for input...</div>
          </div>
        </div>
        {/* Pointer */}
        <div className="edit-agent-pointer" />
        {/* Outer ring */}
        <div className="edit-node-ring" />
        {/* Connection dot */}
        <div className="edit-node-dot" style={{ backgroundColor: color }} />
        {/* Selection indicator */}
        {isSelected && <div className="edit-node-selection-ring" style={{ borderColor: color }} />}
      </div>
    );
  };

  if (loading) {
    return (
      <Box className="proveit-edit-loading">
        <CircularProgress size={100} thickness={5} sx={{ color: '#F2EEA1' }} />
        <Typography fontSize="large" color="#fff" marginTop={4}>Loading editor...</Typography>
      </Box>
    );
  }

  if (!editedMegaFactory) {
    return (
      <Box className="proveit-edit-loading">
        <Typography fontSize="large" color="#fff">Megafactory not found</Typography>
        <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={() => navigate('/proveit')} sx={{ mt: 2 }}>
          Back to ProveIt
        </Button>
      </Box>
    );
  }

  // Get all nodes for current factory
  const dataTags = currentFactory?.dataTags || [];
  const agents = currentFactory?.agents || [];
  const beacons = currentFactory?.Children || [];

  return (
    <Box className="proveit-edit-container">
      {/* Admin Banner */}
      <Paper className="proveit-edit-banner" elevation={3}>
        <Box className="proveit-edit-banner-left">
          <AdminPanelSettingsIcon sx={{ fontSize: 28, color: '#FF9800' }} />
          <Typography variant="h6" fontWeight="bold" sx={{ ml: 1 }}>ADMIN MODE</Typography>
          <Typography variant="subtitle1" sx={{ ml: 2, color: 'text.secondary' }}>ProveIt Configuration Editor</Typography>
        </Box>
        <Box className="proveit-edit-banner-right">
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/proveit')} sx={{ mr: 2 }}>
            Back to ProveIt
          </Button>
          {isDirty && <Typography variant="body2" color="warning.main" sx={{ mr: 2 }}>Unsaved changes</Typography>}
        </Box>
      </Paper>

      {/* Main Content */}
      <Box className="proveit-edit-main">
        {/* Left Panel - MQTT Config */}
        <Paper className="proveit-edit-sidebar" elevation={2}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Shared MQTT Config</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This configuration is used by the UNS sidebar and all factories
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="medium">MQTT Broker Settings</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box className="proveit-edit-form-fields">
                <TextField label="Broker URL" size="small" fullWidth
                  value={editedMegaFactory?.mqttConfig?.broker || ''}
                  onChange={(e) => handleMqttConfigChange('broker', e.target.value)}
                  placeholder="wss://broker.example.com:8884/mqtt"
                />
                <TextField label="Username" size="small" fullWidth
                  value={editedMegaFactory?.mqttConfig?.username || ''}
                  onChange={(e) => handleMqttConfigChange('username', e.target.value)}
                />
                <TextField label="Password" type="password" size="small" fullWidth
                  value={editedMegaFactory?.mqttConfig?.password || ''}
                  onChange={(e) => handleMqttConfigChange('password', e.target.value)}
                />
                <TextField label="Topic Prefix" size="small" fullWidth
                  value={editedMegaFactory?.mqttConfig?.topicPrefix || ''}
                  onChange={(e) => handleMqttConfigChange('topicPrefix', e.target.value)}
                  placeholder="ProveIt"
                />
              </Box>
            </AccordionDetails>
          </Accordion>
          
          <Alert severity="success" sx={{ mt: 2, fontSize: 12 }}>
            MQTT config is persisted to Firestore when you save.
          </Alert>
        </Paper>

        {/* Center - Factory Preview with Draggable Nodes */}
        <Box className="proveit-edit-center">
          <Paper className="proveit-edit-tabs-container" elevation={1}>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} variant="fullWidth">
              {subfactories.map((suite, index) => (
                <Tab key={suite.factoryId} label={suite.factory?.Title || `Factory ${index + 1}`} />
              ))}
            </Tabs>
          </Paper>

          {currentFactory && (
            <Paper className="proveit-edit-factory-preview" elevation={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {currentFactory.Title || 'Factory'} Preview - Drag nodes to reposition
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip size="small" icon={<AppsIcon />} label={`${beacons.length} Apps`} sx={{ bgcolor: 'rgba(33, 150, 243, 0.1)' }} />
                  <Chip size="small" icon={<LocalOfferIcon />} label={`${dataTags.length} Tags`} sx={{ bgcolor: 'rgba(0, 200, 83, 0.1)' }} />
                  <Chip size="small" icon={<SmartToyIcon />} label={`${agents.length} Agents`} sx={{ bgcolor: 'rgba(156, 39, 176, 0.1)' }} />
                </Box>
              </Box>
              
              <Box 
                ref={previewContainerRef}
                className="proveit-edit-factory-preview-container"
                onClick={handlePreviewClick}
              >
                {currentFactoryImageUrl ? (
                  <div className="proveit-edit-unified-preview">
                    {/\.(webm|mp4|mov|mkv)/i.test(currentFactoryImageUrl) ? (
                      <video
                        ref={imageRef}
                        src={currentFactoryImageUrl}
                        className="proveit-edit-factory-image"
                        autoPlay
                        loop
                        muted
                        playsInline
                        draggable={false}
                        style={{ background: 'transparent' }}
                      />
                    ) : (
                      <img
                        ref={imageRef}
                        src={currentFactoryImageUrl}
                        alt={currentFactory.Title || 'Factory'}
                        className="proveit-edit-factory-image"
                        draggable={false}
                      />
                    )}
                    
                    {/* All nodes overlay */}
                    <div className="proveit-edit-nodes-layer">
                      {/* Beacons - render first (lower z-index) */}
                      {beacons.map((beacon) => renderBeaconNode(beacon, selectedNode?.id === beacon.id))}
                      
                      {/* Data Tags */}
                      {dataTags.map((tag) => renderDataTagNode(tag, selectedNode?.id === tag.id))}
                      
                      {/* Agents - render last (higher z-index) */}
                      {agents.map((agent) => renderAgentNode(agent, selectedNode?.id === agent.id))}
                    </div>
                  </div>
                ) : (
                  <Box className="proveit-edit-no-image">
                    <Typography color="text.secondary">No image configured for this factory</Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          )}
        </Box>

        {/* Right Panel - Node Lists & Properties */}
        <Paper className="proveit-edit-panel" elevation={2}>
          {currentFactory ? (
            <>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                {currentFactory.Title || 'Factory'} Nodes
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {/* Factory Settings */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SettingsIcon sx={{ color: '#FF9800', fontSize: 20 }} />
                    <Typography fontWeight="medium">Factory Settings</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 1 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      label="Media URL"
                      size="small"
                      fullWidth
                      value={currentFactory?.mediaUrl || ''}
                      onChange={(e) => updateFactory(currentFactoryId, { mediaUrl: e.target.value })}
                      placeholder="https://example.com/video.webm"
                      helperText="URL of image or video (e.g. .webm, .mkv, .mp4, .png). Use .webm for best transparency support."
                    />
                    {/* File upload row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <input
                        ref={uploadFileRef}
                        type="file"
                        accept="video/*,image/*,.webm,.mkv,.mov,.mp4"
                        style={{ display: 'none' }}
                        onChange={(e) => setSelectedUploadFile(e.target.files?.[0] || null)}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => uploadFileRef.current?.click()}
                      >
                        Choose File
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={isUploading ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
                        onClick={handleFileUpload}
                        disabled={!selectedUploadFile || isUploading}
                      >
                        {isUploading ? 'Uploading...' : 'Upload'}
                      </Button>
                      {selectedUploadFile && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                          {selectedUploadFile.name}
                        </Typography>
                      )}
                      {uploadError && (
                        <Typography variant="caption" color="error">
                          {uploadError}
                        </Typography>
                      )}
                    </Box>
                    <Alert severity="info" sx={{ fontSize: 12 }}>
                      For transparent backgrounds, use WebM with alpha. MKV may contain alpha, but browser playback support is limited and can fail depending on codec/container.
                    </Alert>
                    {selectedUploadFile && isSelectedVideoUpload && !isLikelyAlphaCompatibleUpload && (
                      <Alert severity="warning" sx={{ fontSize: 12 }}>
                        Selected video is .{selectedUploadExtension || 'unknown'}. This format usually does not preserve transparency in browser playback.
                      </Alert>
                    )}
                    <TextField
                      label="Video Control Topic"
                      size="small"
                      fullWidth
                      value={currentFactory?.videoControlTopic || ''}
                      onChange={(e) => updateFactory(currentFactoryId, { videoControlTopic: e.target.value })}
                      placeholder="Enterprise B/Site1/Area1/Line1/Status"
                      helperText="MQTT topic whose value controls video playback"
                    />
                    <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                      <TextField
                        label="Start Value"
                        size="small"
                        fullWidth
                        value={currentFactory?.videoStartValue || ''}
                        onChange={(e) => updateFactory(currentFactoryId, { videoStartValue: e.target.value })}
                        placeholder="Running"
                        helperText="Defaults to 'Running' if empty"
                      />
                      <TextField
                        label="Stop Value"
                        size="small"
                        fullWidth
                        value={currentFactory?.videoStopValue || ''}
                        onChange={(e) => updateFactory(currentFactoryId, { videoStopValue: e.target.value })}
                        placeholder="Stopped"
                        helperText="Defaults to 'Stopped' if empty"
                      />
                    </Stack>
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Selected Node Properties */}
              {selectedNode && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'rgba(0, 0, 0, 0.02)' }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                    Selected: {selectedNode.data?.label || selectedNode.data?.name || selectedNode.data?.Title || 'Node'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Type: {selectedNode.type === NODE_TYPES.BEACON ? 'App Node' : selectedNode.type === NODE_TYPES.DATA_TAG ? 'Data Tag' : 'AI Agent'}
                  </Typography>
                  <Button size="small" startIcon={<EditIcon />} onClick={() => openEditDialog(selectedNode.type, selectedNode.id)}>
                    Edit Properties
                  </Button>
                </Paper>
              )}

              {/* App Nodes (Beacons) */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AppsIcon sx={{ color: '#2196F3', fontSize: 20 }} />
                    <Typography fontWeight="medium">App Nodes ({beacons.length})</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 1 }}>
                  <List dense sx={{ maxHeight: 150, overflow: 'auto' }}>
                    {beacons.map((beacon) => (
                      <ListItem 
                        key={beacon.id} 
                        selected={selectedNode?.id === beacon.id}
                        onClick={() => handleNodeClick({ stopPropagation: () => {} }, NODE_TYPES.BEACON, beacon.id)}
                        sx={{ cursor: 'pointer', borderRadius: 1 }}
                      >
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: beacon.color || '#2196F3', mr: 1 }} />
                        <ListItemText primary={beacon.Title || 'App'} secondary={`${beacon.Left}, ${beacon.Top}`} />
                        <ListItemSecondaryAction>
                          <IconButton size="small" onClick={() => openEditDialog(NODE_TYPES.BEACON, beacon.id)}><EditIcon fontSize="small" /></IconButton>
                          <IconButton size="small" onClick={() => handleDeleteNode(NODE_TYPES.BEACON, beacon.id)}><DeleteIcon fontSize="small" /></IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                  <Button size="small" startIcon={<AddIcon />} onClick={handleAddBeacon} fullWidth sx={{ mt: 1 }}>
                    Add App Node
                  </Button>
                </AccordionDetails>
              </Accordion>

              {/* Data Tags */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocalOfferIcon sx={{ color: '#00C853', fontSize: 20 }} />
                    <Typography fontWeight="medium">Data Tags ({dataTags.length})</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 1 }}>
                  <List dense sx={{ maxHeight: 150, overflow: 'auto' }}>
                    {dataTags.map((tag) => (
                      <ListItem 
                        key={tag.id}
                        selected={selectedNode?.id === tag.id}
                        onClick={() => handleNodeClick({ stopPropagation: () => {} }, NODE_TYPES.DATA_TAG, tag.id)}
                        sx={{ cursor: 'pointer', borderRadius: 1 }}
                      >
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: tag.style?.color || '#00C853', mr: 1 }} />
                        <ListItemText primary={tag.label || 'Tag'} secondary={tag.topicPath || 'No topic'} secondaryTypographyProps={{ noWrap: true, sx: { maxWidth: 120 } }} />
                        <ListItemSecondaryAction>
                          <IconButton size="small" onClick={() => openEditDialog(NODE_TYPES.DATA_TAG, tag.id)}><EditIcon fontSize="small" /></IconButton>
                          <IconButton size="small" onClick={() => handleDeleteNode(NODE_TYPES.DATA_TAG, tag.id)}><DeleteIcon fontSize="small" /></IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                  <Button size="small" startIcon={<AddIcon />} onClick={handleAddDataTag} fullWidth sx={{ mt: 1 }}>
                    Add Data Tag
                  </Button>
                </AccordionDetails>
              </Accordion>

              {/* AI Agents */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SmartToyIcon sx={{ color: '#1976d2', fontSize: 20 }} />
                    <Typography fontWeight="medium">AI Agents ({agents.length})</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 1 }}>
                  <List dense sx={{ maxHeight: 150, overflow: 'auto' }}>
                    {agents.map((agent) => (
                      <ListItem 
                        key={agent.id}
                        selected={selectedNode?.id === agent.id}
                        onClick={() => handleNodeClick({ stopPropagation: () => {} }, NODE_TYPES.AGENT, agent.id)}
                        sx={{ cursor: 'pointer', borderRadius: 1 }}
                      >
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: agent.style?.color || '#1976d2', mr: 1 }} />
                        <ListItemText primary={agent.name || 'Agent'} secondary={agent.topicBase || 'No topic'} secondaryTypographyProps={{ noWrap: true, sx: { maxWidth: 120 } }} />
                        <ListItemSecondaryAction>
                          <IconButton size="small" onClick={() => openEditDialog(NODE_TYPES.AGENT, agent.id)}><EditIcon fontSize="small" /></IconButton>
                          <IconButton size="small" onClick={() => handleDeleteNode(NODE_TYPES.AGENT, agent.id)}><DeleteIcon fontSize="small" /></IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                  <Button size="small" startIcon={<AddIcon />} onClick={handleAddAgent} fullWidth sx={{ mt: 1 }}>
                    Add AI Agent
                  </Button>
                </AccordionDetails>
              </Accordion>
            </>
          ) : (
            <Typography color="text.secondary">Select a factory tab to edit</Typography>
          )}
        </Paper>
      </Box>

      {/* Footer */}
      <Paper className="proveit-edit-footer" elevation={3}>
        <Button variant="outlined" color="secondary" startIcon={<CancelIcon />} onClick={handleDiscard} disabled={!isDirty || saving}>
          Discard Changes
        </Button>
        <Button variant="contained" color="primary" startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />} onClick={handleSave} disabled={!isDirty || saving}>
          {saving ? 'Saving...' : 'Save All Changes'}
        </Button>
      </Paper>

      {/* Edit Node Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit {editingNode?.type === NODE_TYPES.BEACON ? 'App Node' : editingNode?.type === NODE_TYPES.DATA_TAG ? 'Data Tag' : 'AI Agent'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {editingNode?.type === NODE_TYPES.BEACON && (
              <>
                <TextField label="Title" fullWidth value={editingNode?.data?.Title || ''} onChange={(e) => setEditingNode(prev => ({ ...prev, data: { ...prev.data, Title: e.target.value } }))} />
                <TextField label="Color" type="color" fullWidth value={editingNode?.data?.color || '#2196F3'} onChange={(e) => setEditingNode(prev => ({ ...prev, data: { ...prev.data, color: e.target.value } }))} InputProps={{ sx: { height: 56 } }} />
              </>
            )}
            {editingNode?.type === NODE_TYPES.DATA_TAG && (
              <>
                <TextField label="Label" fullWidth value={editingNode?.data?.label || ''} onChange={(e) => setEditingNode(prev => ({ ...prev, data: { ...prev.data, label: e.target.value } }))} />
                <TextField label="MQTT Topic Path" fullWidth value={editingNode?.data?.topicPath || ''} onChange={(e) => setEditingNode(prev => ({ ...prev, data: { ...prev.data, topicPath: e.target.value } }))} placeholder="Enterprise B/Site1/..." />
                <TextField label="Color" type="color" fullWidth value={editingNode?.data?.style?.color || '#00C853'} onChange={(e) => setEditingNode(prev => ({ ...prev, data: { ...prev.data, style: { ...prev.data.style, color: e.target.value } } }))} InputProps={{ sx: { height: 56 } }} />
              </>
            )}
            {editingNode?.type === NODE_TYPES.AGENT && (
              <>
                <TextField label="Name" fullWidth value={editingNode?.data?.name || ''} onChange={(e) => setEditingNode(prev => ({ ...prev, data: { ...prev.data, name: e.target.value } }))} />
                <FormControl fullWidth>
                  <InputLabel id="agent-icon-label">Icon</InputLabel>
                  <Select
                    labelId="agent-icon-label"
                    label="Icon"
                    value={editingNode?.data?.icon || 'public'}
                    onChange={(e) => setEditingNode(prev => ({ ...prev, data: { ...prev.data, icon: e.target.value } }))}
                  >
                    {Object.entries(AGENT_ICONS).map(([key, { label, viewBox, path }]) => (
                      <MenuItem key={key} value={key}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <svg viewBox={viewBox} fill="currentColor" width="20" height="20">
                            <path d={path} />
                          </svg>
                          {label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField label="Topic Base" fullWidth value={editingNode?.data?.topicBase || ''} onChange={(e) => setEditingNode(prev => ({ ...prev, data: { ...prev.data, topicBase: e.target.value } }))} placeholder="agents/my-agent" helperText="Subscribes to /Status and /Thoughts subtopics" />
                <TextField label="Color" type="color" fullWidth value={editingNode?.data?.style?.color || '#1976d2'} onChange={(e) => setEditingNode(prev => ({ ...prev, data: { ...prev.data, style: { ...prev.data.style, color: e.target.value } } }))} InputProps={{ sx: { height: 56 } }} />
                
                {/* Simulated Thoughts Section */}
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" color="text.secondary">Simulated Thoughts</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                  Define custom thoughts for this agent during simulation. Leave empty to use default thoughts based on agent name.
                </Typography>
                
                {/* Existing thoughts as ordered list */}
                <List dense sx={{ maxHeight: 200, overflow: 'auto', bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 1, mb: 1 }}>
                  {(editingNode?.data?.simulatedThoughts || []).length === 0 ? (
                    <ListItem>
                      <ListItemText 
                        secondary="No custom thoughts defined. Add thoughts below or leave empty to use defaults."
                        secondaryTypographyProps={{ sx: { fontStyle: 'italic' } }}
                      />
                    </ListItem>
                  ) : (
                    (editingNode?.data?.simulatedThoughts || []).map((thought, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <Typography variant="body2" sx={{ minWidth: 24, color: 'text.secondary' }}>
                          {index + 1}.
                        </Typography>
                        <ListItemText 
                          primary={thought} 
                          primaryTypographyProps={{ 
                            variant: 'body2',
                            sx: { 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }
                          }}
                        />
                        <ListItemSecondaryAction sx={{ display: 'flex', gap: 0 }}>
                          <IconButton 
                            size="small" 
                            disabled={index === 0}
                            onClick={() => {
                              const newThoughts = [...(editingNode?.data?.simulatedThoughts || [])];
                              [newThoughts[index - 1], newThoughts[index]] = [newThoughts[index], newThoughts[index - 1]];
                              setEditingNode(prev => ({ ...prev, data: { ...prev.data, simulatedThoughts: newThoughts } }));
                            }}
                          >
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            disabled={index === (editingNode?.data?.simulatedThoughts || []).length - 1}
                            onClick={() => {
                              const newThoughts = [...(editingNode?.data?.simulatedThoughts || [])];
                              [newThoughts[index], newThoughts[index + 1]] = [newThoughts[index + 1], newThoughts[index]];
                              setEditingNode(prev => ({ ...prev, data: { ...prev.data, simulatedThoughts: newThoughts } }));
                            }}
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            size="small"
                            onClick={() => {
                              const newThoughts = [...(editingNode?.data?.simulatedThoughts || [])];
                              newThoughts.splice(index, 1);
                              setEditingNode(prev => ({ ...prev, data: { ...prev.data, simulatedThoughts: newThoughts } }));
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))
                  )}
                </List>
                
                {/* Add new thought */}
                <TextField
                  label="Add thought"
                  fullWidth
                  size="small"
                  placeholder="e.g., Analyzing production metrics..."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.target.value.trim()) {
                      e.preventDefault();
                      const newThought = e.target.value.trim();
                      const currentThoughts = editingNode?.data?.simulatedThoughts || [];
                      setEditingNode(prev => ({ 
                        ...prev, 
                        data: { 
                          ...prev.data, 
                          simulatedThoughts: [...currentThoughts, newThought] 
                        } 
                      }));
                      e.target.value = '';
                    }
                  }}
                  helperText="Press Enter to add a thought"
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEditDialog} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProveitEdit;
